import { readMemory, writeMemory } from './visitorMemory';

// The shopper's answer to the cookie notice, and the one place the whole storefront can read it from.
// It sits beside the tags it gates: nothing else in the storefront has an opinion about a refusal.
// It is not stored per element: a shop asks once, however many pages carry the notice, so the key names
// the shop's decision rather than the node that happened to ask for it.

/** What a visitor may have said. `null` is nobody having asked them yet. */
export type ConsentAnswer = 'accepted' | 'rejected';

/** Where the answer lives, and the event fired the moment it changes. */
const CONSENT_KEY = 'consent';
export const CONSENT_EVENT = 'xshop:consent';

/** The event the page fires when a visitor answers, so anything that loads tags can wait for it. */
export type ConsentEvent = CustomEvent<{ answer: ConsentAnswer }>;

// Reads what this visitor already said, or null when they have not been asked yet.
export function readConsent(): ConsentAnswer | null {
  const stored = readMemory(CONSENT_KEY, 'visitor');
  return stored === 'accepted' || stored === 'rejected' ? stored : null;
}

/**
 * Records the answer and tells the page about it. The reject button matters as much as the accept one:
 * it writes `rejected`, and anything that would have loaded a third-party tag is expected to read this
 * before it does — which is what makes the button real rather than decorative.
 */
export function recordConsent(answer: ConsentAnswer): void {
  writeMemory(CONSENT_KEY, 'visitor', answer);
  if (typeof document !== 'undefined') document.documentElement.dataset.consent = answer;
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { answer } }));
}
